import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Logo } from "@/components/BrandGlyph";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus({ state: "error", msg: "Please enter a valid email." });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      const res = await axios.post(`${API}/newsletter`, { email, source: "footer" });
      setStatus({
        state: "success",
        msg: res.data?.already_subscribed
          ? "You're already on the list — thanks."
          : "Thanks — you're on the list.",
      });
      setEmail("");
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Couldn't subscribe — please try again.";
      setStatus({ state: "error", msg: String(detail) });
    }
  };

  return (
    <footer className="bg-navy-deep text-white" data-testid="site-footer">
      <div className="container-pc py-16">
        <div className="grid gap-10 lg:gap-14 md:grid-cols-2 lg:grid-cols-12">
          {/* Brand block */}
          <div className="lg:col-span-4">
            <Logo variant="light" />
            <p className="mt-6 text-white/70 text-sm leading-relaxed max-w-sm">
              PeptiChill products are storage equipment only. Always follow the storage
              instructions provided with your research materials, medicines or
              temperature-sensitive products. PeptiChill does not provide medical advice.
            </p>
          </div>

          {/* Link columns */}
          <div className="lg:col-span-2">
            <h4 className="text-white text-sm font-semibold tracking-wide mb-4">Shop</h4>
            <ul className="space-y-2.5 text-sm text-white/70">
              <li><Link to="/product" className="hover:text-cyan transition-colors" data-testid="footer-link-product">Product</Link></li>
              <li><a href="/#specs" className="hover:text-cyan transition-colors" data-testid="footer-link-specs">Specifications</a></li>
              <li><Link to="/delivery" className="hover:text-cyan transition-colors" data-testid="footer-link-delivery">Delivery</Link></li>
              <li><Link to="/returns" className="hover:text-cyan transition-colors" data-testid="footer-link-returns">Returns</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-white text-sm font-semibold tracking-wide mb-4">Company</h4>
            <ul className="space-y-2.5 text-sm text-white/70">
              <li><Link to="/contact" className="hover:text-cyan transition-colors" data-testid="footer-link-contact">Contact</Link></li>
              <li><Link to="/privacy" className="hover:text-cyan transition-colors" data-testid="footer-link-privacy">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-cyan transition-colors" data-testid="footer-link-terms">Terms</Link></li>
              <li><Link to="/disclaimer" className="hover:text-cyan transition-colors" data-testid="footer-link-disclaimer">Disclaimer</Link></li>
            </ul>
          </div>

          {/* Newsletter */}
          <div className="lg:col-span-4">
            <h4 className="text-white text-sm font-semibold tracking-wide mb-4">
              Clinical storage updates
            </h4>
            <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-2" data-testid="newsletter-form">
              <label htmlFor="newsletter-email" className="sr-only">Email address</label>
              <input
                id="newsletter-email"
                type="email"
                required
                placeholder="your@email.co.uk"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={status.state === "loading"}
                className="flex-1 h-11 rounded-full bg-white/8 border border-white/15 px-4 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-cyan focus:border-cyan transition disabled:opacity-60"
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                disabled={status.state === "loading"}
                className="h-11 rounded-full bg-cyan text-navy-deep px-5 text-sm font-semibold hover:brightness-110 transition disabled:opacity-70"
                data-testid="newsletter-submit-button"
              >
                {status.state === "loading" ? "Subscribing…" : "Subscribe"}
              </button>
            </form>
            {status.state === "success" && (
              <p
                className="mt-3 text-xs text-cyan font-medium"
                data-testid="newsletter-success"
              >
                {status.msg}
              </p>
            )}
            {status.state === "error" && (
              <p
                className="mt-3 text-xs text-red-300"
                data-testid="newsletter-error"
              >
                {status.msg}
              </p>
            )}
            {status.state !== "success" && status.state !== "error" && (
              <p className="mt-3 text-xs text-white/50">
                Get clinical storage updates. No spam.
              </p>
            )}
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10">
          <p className="text-xs text-white/55 leading-relaxed max-w-xl">
            © 2026 PeptiChill. PeptiChill is a trading name operated alongside Peptigen Labs.
          </p>
        </div>
      </div>
    </footer>
  );
}
